import React, { useState } from 'react';
import Button from './Button';
import Modal from './Modal';
import useStyles from '../../theme/useStyles';
import { useChurchConfig } from '../../hooks';
import { printTemplates } from '../../api/endpoints';
import { printDocument, buildPrintHtml, NoTemplateError } from '../../utils/printService';
import type { PrintData } from '../../utils/printService';
import { documentFor } from '../../utils/printDocuments';
import type { DocumentType, PrintTemplate } from '../../types';

/**
 * The Print control every screen uses.
 *
 * A module says which module it is, what kind of document this is, and hands
 * over the record's values. Everything after that — finding the layout the
 * parish configured, filling it, and opening the document — belongs to the
 * print service, so no screen carries a design or a print routine of its own.
 *
 * Whether staff are offered a choice of layout is a property of the document,
 * not of the screen printing it: the registry marks certificates and receipts
 * selectable, and everything else prints with the layout the parish set. A call
 * site can still override it, but it does not have to know the rule.
 */
export interface PrintButtonProps {
  /** Module id as the registry spells it, e.g. 'Baptism'. */
  module: string;
  documentType: DocumentType;
  /** The record's values, named as the template's placeholders name them. */
  data: PrintData | (() => PrintData | Promise<PrintData>);
  /** Overrides the registry's own answer for this document. */
  selectable?: boolean;
  label?: string;
  /** Window title, and the name the browser suggests for a PDF. */
  title?: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  disabled?: boolean;
  onNotify?: (message: string, type?: 'success' | 'error') => void;
  isDarkMode?: boolean;
}

const PrintButton = ({
  module, documentType, data, selectable,
  label = '🖨️ Print / Save PDF', title, variant = 'secondary', size,
  disabled, onNotify, isDarkMode
}: PrintButtonProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;
  const config = useChurchConfig();

  const offersChoice = selectable ?? Boolean(documentFor(documentType)?.selectable);

  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState<PrintTemplate[] | null>(null);
  const [preview, setPreview] = useState<{ template: PrintTemplate; html: string } | null>(null);

  const resolveData = async (): Promise<PrintData> =>
    (typeof data === 'function' ? await data() : data);

  const run = async (template?: PrintTemplate) => {
    setBusy(true);
    try {
      await printDocument({
        module, documentType, data: await resolveData(), template, config, title
      });
    } catch (err) {
      // A parish that has not configured this kind of paper is told exactly
      // where to go, rather than being shown an empty window.
      const message = err instanceof NoTemplateError
        ? err.message
        : (err as Error)?.message || 'Could not produce the document.';
      onNotify?.(message, 'error');
    } finally {
      // Closed whatever happened. These dialogs sit on a full-screen overlay,
      // so one left open by a failed print hides the whole application behind
      // it — the page still navigates underneath, which reads as a dead screen.
      setPreview(null);
      setChoices(null);
      setBusy(false);
    }
  };

  const start = async () => {
    if (!offersChoice) { run(); return; }
    setBusy(true);
    try {
      const usable = await printTemplates.usable(module, documentType);
      const list = Array.isArray(usable) ? usable : [];
      // One layout is not a choice; more than one is.
      if (list.length > 1) setChoices(list);
      else await run(list[0]);
    } catch (err) {
      onNotify?.((err as Error)?.message || 'Could not load the templates.', 'error');
    } finally {
      setBusy(false);
    }
  };

  /**
   * The layout shown against this record's own values, before any paper is
   * used. It is the same HTML the printer is handed — built by the print
   * service, not redrawn here.
   */
  const openPreview = async (template: PrintTemplate) => {
    setBusy(true);
    try {
      const html = buildPrintHtml(template, await resolveData(), config, title);
      setPreview({ template, html });
    } catch (err) {
      onNotify?.((err as Error)?.message || 'Could not build the preview.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant={variant} size={size} disabled={disabled || busy} onClick={start}>
        {busy ? 'Preparing…' : label}
      </Button>

      {choices && (
        <Modal
          title="Choose a template"
          width="520px"
          onClose={() => setChoices(null)}
          isDarkMode={isDarkMode}
          footer={<Button variant="secondary" onClick={() => setChoices(null)}>Cancel</Button>}
        >
          <div style={{ color: t.textMuted, marginBottom: '12px' }}>
            More than one layout is set up for this document. Look at one first, or print
            with it straight away.
          </div>
          {choices.map((tpl) => (
            <div
              key={tpl.id}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: '10px', flexWrap: 'wrap',
                padding: '12px 14px', marginBottom: '8px',
                border: `1px solid ${tpl.isDefault ? t.primary : t.border}`,
                borderRadius: t.radius.md, backgroundColor: t.bgMain, color: t.textMain
              }}
            >
              <div style={{ minWidth: 0 }}>
                <strong>{tpl.templateName}</strong>
                {tpl.isDefault && <span style={{ color: t.primaryText, marginLeft: '8px' }}>· Default</span>}
                <div style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginTop: '2px' }}>
                  {tpl.pageSize || 'A4'} {tpl.orientation === 'LANDSCAPE' ? 'Landscape' : 'Portrait'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => openPreview(tpl)}>
                  Preview
                </Button>
                <Button size="sm" disabled={busy} onClick={() => run(tpl)}>
                  Print
                </Button>
              </div>
            </div>
          ))}
        </Modal>
      )}

      {preview && (
        <Modal
          title={`Preview — ${preview.template.templateName}`}
          width="860px"
          onClose={() => setPreview(null)}
          isDarkMode={isDarkMode}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPreview(null)}>Back</Button>
              <Button disabled={busy} onClick={() => run(preview.template)}>
                Print / Save as PDF
              </Button>
            </>
          }
        >
          {/* The document's own stylesheet only, in its own frame: what is on
              screen here is the file the printer is given. */}
          <iframe
            title="Print preview"
            srcDoc={preview.html}
            style={{
              width: '100%', height: '60vh', border: `1px solid ${t.border}`,
              borderRadius: t.radius.sm, backgroundColor: '#FFF'
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default PrintButton;
